import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();
const errorSchema = z.object({ message: z.string() });

const CouponSchema = z.object({
  id: z.string(),
  code: z.string(),
  description: z.string().nullable(),
  amountOff: z.number().nullable(),
  percentOff: z.number().nullable(),
  currency: z.string().nullable(),
  expiresAt: z.string().nullable(),
  maxRedemptions: z.number().nullable(),
  redeemedCount: z.number(),
  isActive: z.boolean(),
  createdAt: z.string(),
});

const CreateCouponInputSchema = z
  .object({
    code: z.string().min(1).max(50).toUpperCase(),
    description: z.string().optional(),
    amountOff: z.number().positive().optional(),
    percentOff: z.number().positive().max(100).optional(),
    currency: z.string().optional(),
    expiresAt: z.string().optional(),
    maxRedemptions: z.number().int().positive().optional(),
  })
  .refine((d) => (d.amountOff == null) !== (d.percentOff == null), {
    message: "Exactly one of amountOff or percentOff is required",
  })
  .refine((d) => d.amountOff == null || d.currency != null, {
    message: "currency is required when amountOff is set",
  });

const RedeemCouponInputSchema = z.object({
  code: z.string().min(1),
});

const RedeemCouponResponseSchema = z.object({
  couponId: z.string(),
  code: z.string(),
  creditId: z.string(),
  amountApplied: z.number().nullable(),
  percentApplied: z.number().nullable(),
  currency: z.string().nullable(),
  description: z.string().nullable(),
});

export const couponContract = c.router({
  listCoupons: {
    method: "GET",
    path: "/billing/coupons",
    responses: { 200: z.array(CouponSchema), 401: errorSchema, 500: errorSchema },
    summary: "List all coupons",
  },
  createCoupon: {
    method: "POST",
    path: "/billing/coupons",
    body: CreateCouponInputSchema,
    responses: {
      201: CouponSchema,
      400: errorSchema,
      401: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Create a new coupon",
  },
  deactivateCoupon: {
    method: "DELETE",
    path: "/billing/coupons/:couponId",
    pathParams: z.object({ couponId: z.string() }),
    body: z.object({}),
    responses: {
      200: z.object({ id: z.string() }),
      401: errorSchema,
      404: errorSchema,
      500: errorSchema,
    },
    summary: "Deactivate a coupon (not deleted, just disabled)",
  },
  redeemCoupon: {
    method: "POST",
    path: "/billing/franchises/:franchiseId/redeem-coupon",
    pathParams: z.object({ franchiseId: z.string() }),
    body: RedeemCouponInputSchema,
    responses: {
      201: RedeemCouponResponseSchema,
      400: errorSchema,
      401: errorSchema,
      404: errorSchema,
      409: errorSchema,
      500: errorSchema,
    },
    summary: "Redeem a coupon code for a franchise — issues a KB account credit",
  },
});
